import { useEffect, useState, type InputHTMLAttributes } from 'react';
import { Copy, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { InvestmentProduct } from '@/editor/investmentProduct';
import {
  calculateInvestmentProduct,
  formatRange,
  investmentProductCatalogLine,
  investmentProductDescriptionHtml,
  investmentProductRangeVariant,
  resetInvestmentProduct,
} from '@/editor/investmentProduct';
import {
  investmentCellPlainText,
  investmentTitleCellContent,
} from '@/editor/investmentTableCellContent';
import ProductDescriptionEditor from '@/components/products/ProductDescriptionEditor';
import { ProductDisplayControls, ProductPriceSummary } from './ProductPriceControls';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { confirmAction } from '@/lib/confirm';
import { formatCurrency, formatProductPrice } from '@/lib/format';
import { cn } from '@/lib/utils';
import SortablePricingLine from './SortablePricingLine';

// Formulas leave floating point noise (4.800000000000001) that should not show in a field.
const formulaHours = (hours: number) => Math.round(hours * 10000) / 10000;

// Allow clearing a field while typing without committing an invalid quantity.
function NumberField({
  value,
  onValue,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: number | '';
  onValue: (value: string) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDraft(String(value));
  }, [value, focused]);
  return (
    <input
      {...props}
      type="number"
      value={draft}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setDraft(String(value));
      }}
      onChange={(event) => {
        setDraft(event.target.value);
        onValue(event.target.value);
      }}
    />
  );
}

function VariantSelect({
  product,
  onChange,
  className,
}: {
  product: InvestmentProduct;
  onChange: (variantId: string) => void;
  className?: string;
}) {
  return (
    <Select value={product.variantId} onValueChange={onChange}>
      <SelectTrigger aria-label="Variant" className={cn('bg-card text-xs', className)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent data-investment-inspector>
        {product.variants.map((entry) => (
          <SelectItem key={entry.id} value={entry.id}>
            {entry.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const NO_RANGE = 'no-range';

/** Tweede variant voor een van–tot prijs; zonder keuze heeft het product één prijs. */
function RangeVariantSelect({
  product,
  onChange,
  className,
}: {
  product: InvestmentProduct;
  onChange: (rangeVariantId: string | undefined) => void;
  className?: string;
}) {
  return (
    <Select
      value={investmentProductRangeVariant(product)?.id ?? NO_RANGE}
      onValueChange={(value) => onChange(value === NO_RANGE ? undefined : value)}
    >
      <SelectTrigger aria-label="Tot variant" className={cn('bg-card text-xs', className)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent data-investment-inspector>
        <SelectItem value={NO_RANGE}>Geen, vaste prijs</SelectItem>
        {product.variants
          .filter((entry) => entry.id !== product.variantId)
          .map((entry) => (
            <SelectItem key={entry.id} value={entry.id}>
              {entry.name}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}

/** Drops the 'tot' adjustments; they belong to the previous range variant. */
function withoutRangeAdjustments(product: InvestmentProduct): InvestmentProduct {
  const { rangeAmountCents: _amount, ...rest } = product;
  const overrides = Object.fromEntries(
    Object.entries(product.overrides)
      .map(([code, { rangeHours: _hours, ...override }]) => [code, override] as const)
      .filter(([, override]) => Object.keys(override).length > 0)
  );
  return { ...rest, overrides };
}

export default function ProductPricingPopover({
  product,
  currency,
  onChange,
  onClose,
  onRemove,
  layout = 'popover',
  onDuplicate,
}: {
  product: InvestmentProduct;
  currency: string;
  onChange: (product: InvestmentProduct, separateHistory?: boolean) => void;
  onClose: () => void;
  onRemove: () => void;
  layout?: 'popover' | 'panel';
  onDuplicate?: () => void;
}) {
  const [error, setError] = useState('');
  const computed = calculateInvestmentProduct(product);
  const variant = product.variants.find((entry) => entry.id === product.variantId)!;
  const rangeVariant = investmentProductRangeVariant(product);
  const canRange = product.variants.length > 1;
  const included = computed.lines.filter((line) => !line.excluded).length;
  // Van- en tot-uren; laag naar hoog, net als het bedrag, wat de tot-variant ook is.
  const totalHours = [
    computed.lines.filter((line) => !line.excluded).reduce((sum, line) => sum + line.hours, 0),
    ...(computed.rangeTotalCents === null
      ? []
      : [computed.lines.filter((line) => !line.excluded).reduce((sum, line) => sum + (line.rangeHours ?? 0), 0)]),
  ];
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const commit = (next: InvestmentProduct, separateHistory = false) => {
    try {
      calculateInvestmentProduct(next);
      onChange(next, separateHistory);
      setError('');
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Ongeldige prijs');
    }
  };
  // The description is the product's text in the quote, title line included when the catalog
  // has one; the title itself only names the row inside the tool.
  const descriptionHtml = investmentProductDescriptionHtml(product) ?? '';
  const describes = Boolean(
    investmentCellPlainText({
      content: investmentTitleCellContent({ title: '', titleHtml: descriptionHtml }),
    }).trim()
  );
  // A variant brings its own description, so the quote text is replaced as well.
  // No confirmation: the switch is its own undo step.
  const changeVariant = (variantId: string) => {
    const {
      amountCents: _amount,
      rangeAmountCents: _rangeAmount,
      descriptionHtml: _description,
      ...rest
    } = product;
    if (rest.rangeVariantId === variantId) delete rest.rangeVariantId;
    commit({ ...rest, variantId, overrides: {} }, true);
  };
  const changeRangeVariant = (rangeVariantId: string | undefined) => {
    const { rangeVariantId: _range, ...rest } = withoutRangeAdjustments(product);
    commit(rangeVariantId ? { ...rest, rangeVariantId } : rest, true);
  };
  const setField = (
    code: string,
    field: 'hours' | 'rangeHours' | 'rateCents' | 'excluded',
    value: number | boolean | undefined
  ) => {
    const override = { ...product.overrides[code], [field]: value };
    if (value === undefined) delete override[field];
    commit(
      { ...product, overrides: { ...product.overrides, [code]: override } },
      field === 'excluded'
    );
  };
  return (
    <div className={layout === 'panel' ? 'product-pricing-panel' : 'product-pricing-popover'}>
      <div className={layout === 'panel' ? 'product-pricing-content scrollbar-thin' : undefined}>
        <div className="product-pricing-head">
          <div className="min-w-0 flex-1">
            {layout === 'popover' && <p className="product-pricing-eyebrow">Prijsopbouw</p>}
            <strong>{product.productName}</strong>
          </div>
          {computed.adjusted && <span className="investment-product-adjusted">Aangepast</span>}
          {layout === 'popover' && (
            <>
              <VariantSelect
                product={product}
                onChange={changeVariant}
                className="h-8 w-auto max-w-[140px] gap-2"
              />
              {canRange && (
                <>
                  <span className="self-center text-xs text-muted-foreground">tot</span>
                  <RangeVariantSelect
                    product={product}
                    onChange={changeRangeVariant}
                    className="h-8 w-auto max-w-[140px] gap-2"
                  />
                </>
              )}
              <button
                type="button"
                className="product-pricing-close"
                aria-label="Prijsopbouw sluiten"
                onClick={onClose}
              >
                <X size={16} />
              </button>
            </>
          )}
        </div>
        {layout === 'panel' && (
          <div className={cn('workspace-product-fields', canRange && 'has-range')}>
            <label>
              Titel
              <input
                className="workspace-input"
                value={product.productName}
                onChange={(event) => commit({ ...product, productName: event.target.value })}
              />
            </label>
            <label>
              {rangeVariant ? 'Van variant' : 'Variant'}
              <VariantSelect
                product={product}
                onChange={changeVariant}
                className="h-9 font-normal"
              />
            </label>
            {canRange && (
              <label>
                Tot variant
                <RangeVariantSelect
                  product={product}
                  onChange={changeRangeVariant}
                  className="h-9 font-normal"
                />
              </label>
            )}
            <div className="workspace-product-description">
              <span>Omschrijving</span>
              <ProductDescriptionEditor
                id={'product-description-' + product.id}
                value={descriptionHtml}
                onChange={(html) => commit({ ...product, descriptionHtml: html })}
              />
              <p className="workspace-product-hint">
                {describes
                  ? 'Zo staat het product in de offerte. De titel ziet de klant niet.'
                  : 'Leeg: de offerte toont dan de titel.'}
              </p>
            </div>
          </div>
        )}
        <div className="product-pricing-display">
          <span>In de offerte</span>
          <ProductDisplayControls product={product} onChange={(next) => commit(next, true)} />
        </div>
        {product.priceMode === 'hours' && variant.lines.length > 0 ? (
          <>
            {layout === 'panel' && (
              <div className="workspace-price-heading">
                <h3>Prijsopbouw</h3>
                <span>
                  {included} van {computed.lines.length} regels inbegrepen
                </span>
                <button
                  type="button"
                  className="workspace-quiet-button ml-auto"
                  onClick={() => {
                    const line = {
                      code: 'custom_' + crypto.randomUUID().replace(/-/g, '_'),
                      description: 'Nieuwe werkzaamheid',
                      hours: 0,
                      rateCents: variant.lines[0]?.rateCents ?? 0,
                      totalCents: 0,
                    };
                    commit(
                      {
                        ...product,
                        catalogVariants: product.catalogVariants ?? product.variants,
                        variants: product.variants.map((entry) =>
                          entry.id === variant.id
                            ? { ...entry, lines: [...entry.lines, line] }
                            : entry
                        ),
                      },
                      true
                    );
                  }}
                >
                  <Plus size={14} />
                  Regel toevoegen
                </button>
              </div>
            )}
            <div className={rangeVariant ? 'product-pricing-ranged' : undefined}>
              <div className="product-pricing-line product-pricing-cols">
                <span />
                <span>Regel</span>
                {rangeVariant ? (
                  <>
                    <span>Uren van</span>
                    <span>Uren tot</span>
                  </>
                ) : (
                  <span>Uren</span>
                )}
                <span>Tarief</span>
                <span>Bedrag</span>
                <span />
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={({ active, over }) => {
                  if (layout !== 'panel' || !over || active.id === over.id) return;
                  const from = variant.lines.findIndex((line) => line.code === active.id);
                  const to = variant.lines.findIndex((line) => line.code === over.id);
                  if (from < 0 || to < 0) return;
                  commit(
                    {
                      ...product,
                      catalogVariants: product.catalogVariants ?? product.variants,
                      variants: product.variants.map((entry) =>
                        entry.id === variant.id
                          ? { ...entry, lines: arrayMove(entry.lines, from, to) }
                          : entry
                      ),
                    },
                    true
                  );
                }}
              >
                <SortableContext
                  items={variant.lines.map((line) => line.code)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="product-pricing-lines">
                    {computed.lines.map((line) => {
                      const original = variant.lines.find((entry) => entry.code === line.code)!;
                      const catalogLine = investmentProductCatalogLine(product, line.code);
                      const isCustomLine = !catalogLine;
                      const rangeOriginal = rangeVariant?.lines.find(
                        (entry) => entry.code === line.code
                      );
                      const rangeCatalogLine =
                        rangeVariant &&
                        investmentProductCatalogLine(product, line.code, rangeVariant.id);
                      const override = product.overrides[line.code] ?? {};
                      const last = !line.excluded && included === 1;
                      return (
                        <SortablePricingLine
                          key={line.code}
                          code={line.code}
                          description={line.description}
                          enabled={layout === 'panel'}
                          excluded={line.excluded}
                        >
                          <input
                            type="checkbox"
                            aria-label={line.description + ' meetellen'}
                            checked={!line.excluded}
                            disabled={last}
                            onChange={(event) =>
                              setField(line.code, 'excluded', !event.target.checked)
                            }
                          />
                          <div className="min-w-0">
                            {isCustomLine ? (
                              <input
                                className="workspace-input w-full"
                                aria-label="Omschrijving nieuwe werkzaamheid"
                                value={line.description}
                                onChange={(event) =>
                                  commit({
                                    ...product,
                                    variants: product.variants.map((entry) =>
                                      entry.id === variant.id
                                        ? {
                                            ...entry,
                                            lines: entry.lines.map((item) =>
                                              item.code === line.code
                                                ? { ...item, description: event.target.value }
                                                : item
                                            ),
                                          }
                                        : entry
                                    ),
                                  })
                                }
                              />
                            ) : (
                              <span>{line.description}</span>
                            )}
                            {line.excluded ? (
                              <small>Uitgesloten, telt niet mee</small>
                            ) : last ? (
                              <small>Laatste regel, kan niet uit</small>
                            ) : original.formula && override.hours === undefined ? (
                              <small className="product-formula">
                                ƒx {original.formula.slice(1)}
                              </small>
                            ) : null}
                            {catalogLine && override.hours !== undefined && (
                              <small className="product-price-adjusted">
                                Catalogus {catalogLine.hours} uur{' '}
                                {catalogLine.formula ? '· formule overschreven' : ''}
                                <button
                                  type="button"
                                  aria-label={line.description + ' uren terugzetten'}
                                  onClick={() => setField(line.code, 'hours', undefined)}
                                >
                                  <RotateCcw size={12} />
                                </button>
                              </small>
                            )}
                            {rangeCatalogLine && override.rangeHours !== undefined && (
                              <small className="product-price-adjusted">
                                Catalogus tot {rangeCatalogLine.hours} uur
                                <button
                                  type="button"
                                  aria-label={line.description + ' tot-uren terugzetten'}
                                  onClick={() => setField(line.code, 'rangeHours', undefined)}
                                >
                                  <RotateCcw size={12} />
                                </button>
                              </small>
                            )}
                            {catalogLine && override.rateCents !== undefined && (
                              <small className="product-price-adjusted">
                                Catalogus {formatCurrency(catalogLine.rateCents / 100, currency)}
                                <button
                                  type="button"
                                  aria-label={line.description + ' tarief terugzetten'}
                                  onClick={() => setField(line.code, 'rateCents', undefined)}
                                >
                                  <RotateCcw size={12} />
                                </button>
                              </small>
                            )}
                          </div>
                          <NumberField
                            aria-label={line.description + ' uren'}
                            min="0"
                            step="any"
                            disabled={line.excluded}
                            className={
                              !isCustomLine && override.hours !== undefined
                                ? 'is-adjusted'
                                : original.formula
                                  ? 'is-formula'
                                  : ''
                            }
                            value={formulaHours(line.excluded ? original.hours : line.hours)}
                            onValue={(value) => {
                              if (value !== '') setField(line.code, 'hours', Number(value));
                            }}
                          />
                          {rangeVariant && (
                            <NumberField
                              aria-label={line.description + ' uren tot'}
                              min="0"
                              step="any"
                              disabled={line.excluded}
                              className={
                                rangeCatalogLine && override.rangeHours !== undefined
                                  ? 'is-adjusted'
                                  : rangeOriginal?.formula
                                    ? 'is-formula'
                                    : ''
                              }
                              value={formulaHours(
                                line.excluded
                                  ? (rangeOriginal ?? original).hours
                                  : (line.rangeHours ?? line.hours)
                              )}
                              onValue={(value) => {
                                if (value !== '') setField(line.code, 'rangeHours', Number(value));
                              }}
                            />
                          )}
                          <NumberField
                            aria-label={line.description + ' tarief'}
                            min="0"
                            step="0.01"
                            disabled={line.excluded}
                            className={
                              !isCustomLine && override.rateCents !== undefined ? 'is-adjusted' : ''
                            }
                            value={line.rateCents / 100}
                            onValue={(value) => {
                              if (value !== '')
                                setField(line.code, 'rateCents', Math.round(Number(value) * 100));
                            }}
                          />
                          <span className="text-right font-semibold tabular-nums">
                            {formatCurrency(line.totalCents / 100, currency)}
                            {line.rangeTotalCents !== undefined && (
                              <span className="product-pricing-range-amount">
                                tot {formatCurrency(line.rangeTotalCents / 100, currency)}
                              </span>
                            )}
                          </span>
                        </SortablePricingLine>
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </>
        ) : (
          <div className="product-pricing-amount">
            <label>
              {rangeVariant ? 'Bedrag van' : 'Bedrag'}
              <NumberField
                min="0"
                step="0.01"
                aria-label="Bedrag in de offerte"
                placeholder={computed.priceText ?? 'Op aanvraag'}
                value={
                  product.amountCents != null
                    ? product.amountCents / 100
                    : variant.priceCents === null
                      ? ''
                      : variant.priceCents / 100
                }
                onValue={(value) => {
                  const { amountCents: _amount, ...rest } = product;
                  commit(
                    value === '' ? rest : { ...rest, amountCents: Math.round(Number(value) * 100) }
                  );
                }}
              />
            </label>
            {rangeVariant && (
              <label>
                Bedrag tot
                <NumberField
                  min="0"
                  step="0.01"
                  aria-label="Tot-bedrag in de offerte"
                  placeholder={computed.priceText ?? 'Op aanvraag'}
                  value={
                    product.rangeAmountCents != null
                      ? product.rangeAmountCents / 100
                      : rangeVariant.priceCents === null
                        ? ''
                        : rangeVariant.priceCents / 100
                  }
                  onValue={(value) => {
                    const { rangeAmountCents: _amount, ...rest } = product;
                    commit(
                      value === ''
                        ? rest
                        : { ...rest, rangeAmountCents: Math.round(Number(value) * 100) }
                    );
                  }}
                />
              </label>
            )}
          </div>
        )}
        <div className="product-pricing-adjustments">
          <label>
            Aantal{' '}
            <NumberField
              aria-label="Aantal"
              min="0.01"
              step="any"
              value={product.quantity}
              onValue={(value) => {
                if (value !== '') commit({ ...product, quantity: Number(value) });
              }}
            />
          </label>
          <label>
            Korting (%){' '}
            <NumberField
              aria-label="Korting (%)"
              min="0"
              max="100"
              step="any"
              value={product.discountPct}
              onValue={(value) => {
                if (value !== '') commit({ ...product, discountPct: Number(value) });
              }}
            />
          </label>
        </div>
        {error && (
          <p role="alert" className="px-5 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
      </div>
      {layout === 'panel' && (
        <div className="workspace-product-total">
          <strong>Producttotaal</strong>
          <span>
            {formatRange(Math.min(...totalHours), Math.max(...totalHours), (hours) =>
              hours.toLocaleString('nl-NL')
            )}{' '}
            uur inbegrepen · excl. btw
          </span>
          <strong>{formatProductPrice(computed, currency)}</strong>
        </div>
      )}
      {layout === 'popover' && <ProductPriceSummary product={product} currency={currency} />}
      <div className="product-pricing-foot">
        {onDuplicate && (
          <Button variant="ghost" size="sm" onClick={onDuplicate}>
            <Copy size={14} />
            Dupliceren
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={async () => {
            const reset = resetInvestmentProduct(product);
            if (
              (computed.adjusted ||
                product.catalogVariants ||
                product.quantity !== 1 ||
                product.discountPct !== 0 ||
                product.amountCents !== undefined ||
                product.rangeAmountCents !== undefined ||
                product.amountText !== undefined) &&
              !(await confirmAction({
                title: 'Catalogusprijs herstellen?',
                description:
                  'Aangepaste rekenregels, toegevoegde regels, aantal en korting worden teruggezet. Je kunt dit ongedaan maken.',
                confirmLabel: 'Herstellen',
              }))
            )
              return;
            commit(reset, true);
          }}
        >
          <RotateCcw size={14} />
          Naar catalogusprijs
        </Button>
        {(layout === 'panel' || included === 1) && (
          <Button variant="ghost" size="sm" className="text-red-600" onClick={onRemove}>
            <Trash2 size={14} />
            Verwijderen
          </Button>
        )}
        {layout === 'popover' && (
          <span className="flex-1 text-xs text-muted-foreground">
            Wijzigingen staan direct in de offerte
          </span>
        )}
        {layout === 'popover' && (
          <Button size="sm" onClick={onClose}>
            Klaar
          </Button>
        )}
      </div>
    </div>
  );
}
